import React, { useState } from 'react';
import { Eye, EyeSlash, Translate } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import Config from '../config/Config';
import { fetchJson, getFriendlyErrorMessage } from '../utils/api';
import { setExternalAccess } from '../utils/externalAccess';

export default function LoginPage() {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const sessionExpired = searchParams.get('reason') === 'expired';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setError('');
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const response = await fetchJson(Config.CrmLoginUrl, {
        method: 'POST',
        body: formData,
      });
      if (!response?.access_token) throw new Error('CRM access token missing');
      setExternalAccess(response.access_token);
      const target = searchParams.get('next');
      navigate(target && target.startsWith('/') && !target.startsWith('//') ? target : '/dashboard', { replace: true });
    } catch (loginError) {
      setStatus('error');
      setError(getFriendlyErrorMessage(loginError, {
        auth: t.loginFailed,
        network: t.backendUnavailable,
        server: t.serverError,
        generic: t.loginFailed,
      }));
    }
  };

  const handleBackToTarget = () => {
    const target = searchParams.get('next');
    if (target && target.startsWith('/') && !target.startsWith('//')) navigate(target);
  };

  return (
    <main className="auth">
      <div className="auth__bar">
        <span className="nav__brand"><span className="nav__mark" aria-hidden="true">TK</span><span>App Builder</span></span>
        <button className="nav__action" type="button" onClick={toggleLanguage} aria-label={t.toggleLanguage}>
          <Translate size={18} aria-hidden="true" />
          <span>{language === 'en' ? 'ID' : 'EN'}</span>
        </button>
      </div>
      <form className="card auth__card stack" onSubmit={handleSubmit}>
        <div className="stack stack--sm">
          <p className="page-head__eyebrow">{t.loginKicker}</p>
          <h1>{t.loginTitle}</h1>
          <p>{t.loginDescription}</p>
        </div>
        <div className="field">
          <label className="label" htmlFor="login-username">{t.username}</label>
          <input id="login-username" className="input" name="username" type="text" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder={t.usernamePlaceholder} required />
        </div>
        <div className="field">
          <label className="label" htmlFor="login-password">{t.loginPassword}</label>
          <div className="input-wrap">
            <input id="login-password" className="input" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t.passwordPlaceholder} required />
            <button type="button" className="input-wrap__btn" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? t.hidePassword : t.showPassword}>
              {showPassword ? <EyeSlash size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
            </button>
          </div>
        </div>
        <p
          className={status === 'error' ? 'notice notice--error' : sessionExpired ? 'notice notice--warn' : 'hint'}
          role={status === 'error' ? 'alert' : 'status'}
        >
          {status === 'error' ? error : sessionExpired ? t.sessionExpired : t.loginSecureNote}
        </p>
        <button className="btn btn--primary" type="submit" disabled={status === 'loading'}>{status === 'loading' ? t.loggingIn : t.login}</button>
        {searchParams.get('next') && <button className="btn btn--text" type="button" onClick={handleBackToTarget}>{t.backToApplication}</button>}
        <p className="hint">{t.companyName} · {t.loginFooter}</p>
      </form>
    </main>
  );
}
